import { gatewayApi } from '@/lib/hrApi';
import type { Facet, FacetOption, FacetPerson, RuleNode } from '@/features/shared/components/conditionGroup';

// --- Programs + Sessions (L&D Phase 1) shared vocabulary ---------------------
export type AssignmentType = 'required' | 'recommended' | 'optional';
export type AudienceMode = 'dynamic' | 'fixed';
export type ProgramStatus = 'draft' | 'published' | 'active' | 'completed' | 'archived';
export type SessionStatus = 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
export type DeliveryMode = 'in_person' | 'virtual' | 'hybrid' | 'self_paced';
export type JourneyStatus = 'completed' | 'attended' | 'missed' | 'in_progress' | 'upcoming' | 'past' | 'cancelled';
/** `op` arrives from Phase 2 rules ('in', 'contains', 'subtree_of', … or 'not <op>' when a NOT matched). */
export interface MatchedCondition { field: string; op?: string; value: string; label: string }
/** Why a person is on a session — evaluated server-side, never re-derived here. */
export interface AssignmentReason {
  type: 'AUDIENCE_RULE' | 'MANUAL_INCLUDE';
  rule_id?: string | null;
  rule_name?: string | null;
  matched_conditions?: MatchedCondition[];
  included_by?: string;
  note?: string;
}

// Learning & Development — talks to learning-service via the gateway
// (/api/v1/learning/*). Rewards program Phase 4 (its own bounded context).
const BASE = '/api/v1/learning';
const data = <T,>(p: Promise<{ data: T }>) => p.then((r) => r.data);

export interface Course {
  id: string; title: string; description?: string;
  provider_kind: 'internal' | 'external' | 'manual'; provider_name?: string; url?: string;
  duration_hours?: number | null; validity_months?: number | null;
  competencies?: string[]; external_ref?: string;
  active: boolean; created_at: string;
}
export interface Assignment {
  id: string;
  /** null for session assignments (Programs + Sessions phase). */
  course_id?: string | null; course_title?: string; user_id: string;
  assigned_by?: string | null; source: string; due_date?: string | null;
  /** Session's IANA zone for session-bound assignments — render due dates on that clock. */
  session_timezone?: string;
  status: 'assigned' | 'in_progress' | 'completed' | 'verified' | 'cancelled';
  note?: string; evidence_url?: string; score?: number | null;
  completed_at?: string | null; verified_at?: string | null; created_at: string;
  // Session assignments
  session_id?: string | null; program_id?: string | null;
  assignment_type?: AssignmentType; reason?: AssignmentReason | null;
  cycle_key?: string | null; cancelled_at?: string | null;
}
export interface Certification {
  id: string; user_id: string; name: string; issuer?: string; number?: string;
  issued_on?: string | null; expires_on?: string | null; evidence_url?: string;
  course_id?: string | null; created_at: string;
}

export const listCourses = () => data<Course[]>(gatewayApi.get(`${BASE}/courses`));
export const createCourse = (b: Partial<Course>) => data<Course>(gatewayApi.post(`${BASE}/courses`, b));
export const deactivateCourse = (id: string) => gatewayApi.delete(`${BASE}/courses/${id}`).then((r) => r.data);
export const listAssignments = (all?: boolean) => data<Assignment[]>(gatewayApi.get(`${BASE}/assignments`, { params: all ? { all: 'true' } : {} }));
export const assignCourse = (b: { course_id: string; user_id: string; employee_id?: string; due_date?: string; note?: string }) =>
  data<Assignment>(gatewayApi.post(`${BASE}/assignments`, b));
/** Phase 4: a policy-bound assignment answers 400 "completion policy not satisfied: …" (evidence in the body is still recorded); `force: true` is learning:manage only (403 otherwise) and is audited. */
export const completeAssignment = (id: string, b: { evidence_url?: string; note?: string; score?: number; force?: boolean }) =>
  data<Assignment>(gatewayApi.post(`${BASE}/assignments/${id}/complete`, b));
export const verifyAssignment = (id: string) => data<Assignment>(gatewayApi.post(`${BASE}/assignments/${id}/verify`, {}));
export const listCertifications = (all?: boolean) => data<Certification[]>(gatewayApi.get(`${BASE}/certifications`, { params: all ? { all: 'true' } : {} }));
export const addCertification = (b: Partial<Certification>) => data<Certification>(gatewayApi.post(`${BASE}/certifications`, b));

// --- L&D expansion: requirements ---------------------------------------------
export interface LearningRequirement {
  id: string; course_id: string; course_title?: string; name: string;
  departments: string[]; employment_types: string[];
  on_onboarding: boolean; all_active: boolean; due_days: number;
  renew_months?: number | null; active: boolean; created_at: string;
}
export const listRequirements = () => data<LearningRequirement[]>(gatewayApi.get(`${BASE}/requirements`));
export const createRequirement = (b: Partial<LearningRequirement>) => data<LearningRequirement>(gatewayApi.post(`${BASE}/requirements`, b));
export const deactivateRequirement = (id: string) => gatewayApi.delete(`${BASE}/requirements/${id}`).then((r) => r.data);
export const syncRequirements = () => data<{ assigned: number; renewals: number; skipped: number }>(gatewayApi.post(`${BASE}/requirements/sync`, {}));

// --- L&D Phase 1: programs, sessions, participants, journey ------------------
export interface ProgramType { id: string; key: string; label: string; custom: boolean }
export interface Program {
  id: string; name: string; description: string; program_type: string; status: ProgramStatus;
  starts_at?: string | null; ends_at?: string | null; timezone: string;
  audience_mode: AudienceMode; default_assignment_type: AssignmentType;
  published_at?: string | null; archived_at?: string | null; created_at: string; updated_at: string;
  session_count: number; participant_count: number;
  // Phase 5: template lineage + readiness policy (template_update_available only on GET /programs/{id}).
  template_source_id?: string | null; template_version?: number | null;
  readiness_policy?: ReadinessPolicy | null; template_update_available?: boolean;
}
export interface ProgramInput {
  name: string; description?: string; program_type?: string; starts_at?: string; ends_at?: string;
  timezone?: string; audience_mode?: AudienceMode; default_assignment_type?: AssignmentType;
}
export interface SessionMaterial { title: string; url: string; kind?: string }
export interface SessionAudience {
  departments: string[]; employment_types: string[];
  assignment_type?: AssignmentType; mode?: AudienceMode;
}
export interface SessionInput {
  course_id?: string; title: string; description?: string; sequence?: number;
  starts_at: string; ends_at: string; timezone?: string; delivery_mode: DeliveryMode;
  location?: string; meeting_url?: string; meeting_provider?: string;
  trainer_user_id?: string; trainer_label?: string; capacity?: number;
  attendance_policy?: string; objectives?: string; materials?: SessionMaterial[];
  audience?: SessionAudience;
  /** Phase 3: mint a Meet/Teams link on publish when no link was pasted (virtual/hybrid). */
  generate_meeting_link?: boolean;
  /** Phase 4: completion policy / practical exercise bound to the session (null clears). A track binding overrides these. */
  completion_policy_id?: string | null; practice_id?: string | null;
  /** Phase 5: counts toward readiness when the program's policy requires critical sessions. */
  critical?: boolean;
}
/** Phase 3 attendance vocabulary — set server-side (self check-in, trainer marks); never derived here. */
export type AttendanceStatus = 'invited' | 'confirmed' | 'attended' | 'partially_attended' | 'absent' | 'excused' | 'cancelled';
export type MirrorStatus = 'mirrored' | 'error' | 'cancelled';
export interface SessionMe {
  assignment_id?: string; assignment_type: AssignmentType; reason?: AssignmentReason | null;
  status: Assignment['status']; completed_at?: string | null;
  track_id?: string | null; track_name?: string | null; attendance?: AttendanceStatus | null;
}
export interface Session {
  id: string; program_id: string; program_name: string; course_id?: string | null;
  title: string; description: string; sequence: number; starts_at: string; ends_at: string;
  timezone: string; delivery_mode: DeliveryMode; location: string; meeting_url: string;
  meeting_provider: string; trainer_user_id?: string | null; trainer_label: string;
  capacity?: number | null; status: SessionStatus; attendance_policy: string; objectives: string;
  materials: SessionMaterial[]; audience: SessionAudience; resolved_at?: string | null;
  cancelled_at?: string | null; cancel_reason: string; created_at: string;
  participant_count: number; me?: SessionMe | null;
  // Phase 3: tracks, champions, calendar mirror
  generate_meeting_link?: boolean; calendar_event_id?: string | null;
  mirror_status?: MirrorStatus | null; mirror_error?: string | null;
  /** Non-admin viewers get tracks without rule/participant_count. */
  tracks?: Track[]; champions?: Champion[];
  // Phase 4: completion policy / practice bound at session level (a track binding wins for its members).
  completion_policy_id?: string | null; practice_id?: string | null;
  /** Phase 5: critical for readiness. */
  critical?: boolean;
}
export interface MySession extends Session { journey_status: JourneyStatus }
export interface Participant {
  id: string; session_id: string; user_id: string; employee_id?: string | null; name: string;
  department: string; assignment_type: AssignmentType; reason?: AssignmentReason | null;
  resolved_at: string; resolution_mode: string; removed_at?: string | null;
  assignment_id?: string | null; status: string;
  /** Phase 3 (optional on the wire): the track the server placed/pinned this person on. */
  track_id?: string | null; track_name?: string | null; track_pinned?: boolean;
}
export interface AudiencePreview {
  total: number;
  by_department: Array<{ department: string; count: number }>;
  people: Array<{ user_id: string; name: string; department: string; matched_conditions: MatchedCondition[]; manual: boolean }>;
  outside_scope: number;
}
export interface Journey {
  programs: Array<{ program: Program; sessions: MySession[]; done: number; total: number }>;
  next?: MySession | null;
}
export interface LearningAction {
  /** Phase 4: 'practice' = a practical exercise to do (or resubmit) — carries assignment_id; session_id is null for course-only assignments. */
  kind: 'prepare' | 'join' | 'complete' | 'overdue' | 'practice'; title: string;
  session_id?: string | null; assignment_id?: string | null; due_at?: string | null; required: boolean;
  /** The session's IANA zone — due_at renders on that clock. */
  timezone?: string | null;
}
export interface LearningSettings {
  default_assignment_type: AssignmentType; default_audience_mode: AudienceMode;
  attendance_threshold_pct: number; reminder_offsets_minutes: number[];
  allow_self_enrollment: boolean; allow_manager_assignment: boolean; allow_withdraw_optional: boolean;
  notify_manager_on_missed_required: boolean; certification_enabled: boolean; readiness_enabled: boolean;
}
export interface PublishResult { program: Program; sessions: number; participants: number; skipped: number }

export const listProgramTypes = () => data<ProgramType[]>(gatewayApi.get(`${BASE}/program-types`));
export const createProgramType = (b: { key: string; label: string }) => data<ProgramType>(gatewayApi.post(`${BASE}/program-types`, b));
export const listPrograms = (status?: string) => data<Program[]>(gatewayApi.get(`${BASE}/programs`, { params: status ? { status } : {} }));
export const getProgram = (id: string) => data<Program>(gatewayApi.get(`${BASE}/programs/${id}`));
export const createProgram = (b: ProgramInput) => data<Program>(gatewayApi.post(`${BASE}/programs`, b));
export const updateProgram = (id: string, b: Partial<ProgramInput>) => data<Program>(gatewayApi.patch(`${BASE}/programs/${id}`, b));
export const publishProgram = (id: string) => data<PublishResult>(gatewayApi.post(`${BASE}/programs/${id}/publish`, {}));
export const archiveProgram = (id: string) => data<Program>(gatewayApi.post(`${BASE}/programs/${id}/archive`, {}));
export const listProgramSessions = (programId: string) => data<Session[]>(gatewayApi.get(`${BASE}/programs/${programId}/sessions`));
export const createSession = (programId: string, b: SessionInput) => data<Session>(gatewayApi.post(`${BASE}/programs/${programId}/sessions`, b));
export const getSession = (id: string) => data<Session>(gatewayApi.get(`${BASE}/sessions/${id}`));
export const updateSession = (id: string, b: Partial<SessionInput>) => data<Session>(gatewayApi.patch(`${BASE}/sessions/${id}`, b));
export const cancelSession = (id: string, reason: string) => data<Session>(gatewayApi.post(`${BASE}/sessions/${id}/cancel`, { reason }));
export const resolveSession = (id: string) => data<{ added: number; skipped: number }>(gatewayApi.post(`${BASE}/sessions/${id}/resolve`, {}));
export const listParticipants = (sessionId: string) => data<Participant[]>(gatewayApi.get(`${BASE}/sessions/${sessionId}/participants`));
export const includeParticipants = (sessionId: string, b: { user_ids: string[]; assignment_type?: AssignmentType; note?: string }) =>
  data<Participant[]>(gatewayApi.post(`${BASE}/sessions/${sessionId}/participants`, b));
export const excludeParticipant = (sessionId: string, userId: string, reason?: string) =>
  gatewayApi.delete(`${BASE}/sessions/${sessionId}/participants/${userId}`, { params: reason ? { reason } : {} }).then((r) => r.data);
/** Preview who a rule matches today. A Phase 2 `rule` tree wins over the legacy `audience` facets when both are sent. */
export const previewAudience = (b: { audience?: { departments: string[]; employment_types: string[] }; rule?: RuleNode | null; user_ids?: string[] }) =>
  data<AudiencePreview>(gatewayApi.post(`${BASE}/audience/preview`, b));
export const listMySessions = (from?: string, to?: string) =>
  data<MySession[]>(gatewayApi.get(`${BASE}/sessions/mine`, { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }));
export const listSessions = (from?: string, to?: string) =>
  data<Session[]>(gatewayApi.get(`${BASE}/sessions`, { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }));
export const getMyJourney = () => data<Journey>(gatewayApi.get(`${BASE}/me/journey`));
export const getMyActions = () => data<LearningAction[]>(gatewayApi.get(`${BASE}/me/actions`));
export const getLearningSettings = () => data<LearningSettings>(gatewayApi.get(`${BASE}/settings`));
export const updateLearningSettings = (b: Partial<LearningSettings>) => data<LearningSettings>(gatewayApi.put(`${BASE}/settings`, b));

// --- L&D Phase 2: generalized audience rules ---------------------------------
// A rule is an AND/OR/NOT tree over the facets the backend advertises (see
// conditionGroup.ts for the shape). A program rule is the default for every
// session; a session rule overrides it; Phase 1 per-session facets remain the
// fallback. Matching always happens server-side.
export type { RuleNode } from '@/features/shared/components/conditionGroup';
export interface AudienceFacets {
  facets: Facet[];
  options: Record<string, FacetOption[]>;
  /** Already narrowed to the caller's reach. */
  people: FacetPerson[];
}
export interface AudienceRule {
  id: string; name: string; owner_kind: 'program' | 'session'; owner_id: string; rule: RuleNode;
  assignment_type?: AssignmentType | ''; mode?: AudienceMode | ''; updated_at: string;
}
/** `rule: null` removes the owner's rule (session falls back to the program rule / legacy facets). */
export interface AudienceRuleInput { name: string; rule: RuleNode | null; assignment_type?: AssignmentType; mode?: AudienceMode }
export interface AudienceRuleResult { rule: AudienceRule | null; resolved: { sessions: number; added: number; skipped: number } }

const asRule = (r: AudienceRule | { rule: null } | null | undefined): AudienceRule | null => (r && r.rule ? (r as AudienceRule) : null);
export const getAudienceFacets = () => data<AudienceFacets>(gatewayApi.get(`${BASE}/audience/facets`));
export const getProgramRule = (programId: string) => data<AudienceRule | { rule: null }>(gatewayApi.get(`${BASE}/programs/${programId}/audience`)).then(asRule);
export const putProgramRule = (programId: string, b: AudienceRuleInput) => data<AudienceRuleResult>(gatewayApi.put(`${BASE}/programs/${programId}/audience`, b));
export const getSessionRule = (sessionId: string) => data<AudienceRule | { rule: null }>(gatewayApi.get(`${BASE}/sessions/${sessionId}/audience`)).then(asRule);
export const putSessionRule = (sessionId: string, b: AudienceRuleInput) => data<AudienceRuleResult>(gatewayApi.put(`${BASE}/sessions/${sessionId}/audience`, b));

// --- L&D Phase 3: tracks, attendance, champions, calendar mirror ------------
// Tracks split one session's roster into named streams. Membership is the
// server's: the first track (by sequence) whose rule matches wins, a track
// without a rule is the default, and pinned people are never re-derived.
export interface Track {
  id: string; session_id: string; name: string; description?: string; sequence: number;
  assignment_type?: AssignmentType | '' | null; objectives?: string; instructions?: string;
  /** Absent for non-admin viewers. */
  rule?: RuleNode | null; participant_count?: number; created_at: string;
  /** Phase 4: bindings that override the session's for this track's members. */
  completion_policy_id?: string | null; practice_id?: string | null;
}
/** `rule` absent = keep (update); null = remove; a tree = replace. */
export interface TrackInput {
  name: string; description?: string; sequence?: number; assignment_type?: AssignmentType;
  objectives?: string; instructions?: string; rule?: RuleNode | null; rule_name?: string;
  /** Phase 4: absent = keep; null = clear; uuid = bind (overrides the session binding). */
  completion_policy_id?: string | null; practice_id?: string | null;
}
export interface TrackSummaryRow { track_id?: string | null; track_name: string; count: number; attended: number; completed: number }
export interface Attendance {
  user_id: string; name: string; department: string; track_id?: string | null; track_name: string;
  assignment_type: AssignmentType; status: AttendanceStatus; duration_minutes?: number | null; source: string; note: string;
  marked_by?: string | null; marked_at?: string | null; completion_state: string;
}
export interface AttendanceMark { user_id: string; status: AttendanceStatus; duration_minutes?: number; note?: string }
export type ChampionScopeKind = 'program' | 'session' | 'track' | 'department';
export interface Champion { id: string; user_id: string; name: string; scope_kind: ChampionScopeKind; scope_id: string; label?: string }
export interface ChampionInput { user_id: string; scope_kind: ChampionScopeKind; scope_id: string; label?: string }
export interface CalendarSyncResult { mirror_status: MirrorStatus; mirror_error: string; calendar_event_id: string; meeting_url: string }

export const listTracks = (sessionId: string) => data<Track[]>(gatewayApi.get(`${BASE}/sessions/${sessionId}/tracks`));
export const createTrack = (sessionId: string, b: TrackInput) => data<Track>(gatewayApi.post(`${BASE}/sessions/${sessionId}/tracks`, b));
export const updateTrack = (trackId: string, b: Partial<TrackInput>) => data<Track>(gatewayApi.patch(`${BASE}/tracks/${trackId}`, b));
export const deleteTrack = (trackId: string) => gatewayApi.delete(`${BASE}/tracks/${trackId}`).then((r) => r.data);
/** Pin (track_id) or unpin (null) one participant; pinned people are never re-derived. */
export const pinParticipantTrack = (sessionId: string, userId: string, trackId: string | null) =>
  gatewayApi.put(`${BASE}/sessions/${sessionId}/participants/${userId}/track`, { track_id: trackId }).then((r) => r.data);
export const getTrackSummary = (sessionId: string) => data<TrackSummaryRow[]>(gatewayApi.get(`${BASE}/sessions/${sessionId}/track-summary`));
export const listAttendance = (sessionId: string) => data<Attendance[]>(gatewayApi.get(`${BASE}/sessions/${sessionId}/attendance`));
export const markAttendance = (sessionId: string, marks: AttendanceMark[]) => data<Attendance[]>(gatewayApi.put(`${BASE}/sessions/${sessionId}/attendance`, { marks }));
/** Participant self-service: confirm before start, check in from 15 min before start until the end (400 otherwise). */
export const selfAttendance = (sessionId: string, action: 'confirm' | 'checkin') => data<Attendance>(gatewayApi.post(`${BASE}/sessions/${sessionId}/attendance/self`, { action }));
export const listChampions = (scopeKind: ChampionScopeKind, scopeId: string) =>
  data<Champion[]>(gatewayApi.get(`${BASE}/champions`, { params: { scope_kind: scopeKind, scope_id: scopeId } }));
export const addChampion = (b: ChampionInput) => data<Champion>(gatewayApi.post(`${BASE}/champions`, b));
export const removeChampion = (id: string) => gatewayApi.delete(`${BASE}/champions/${id}`).then((r) => r.data);
export const syncSessionCalendar = (sessionId: string) => data<CalendarSyncResult>(gatewayApi.post(`${BASE}/sessions/${sessionId}/calendar-sync`, {}));

// --- L&D Phase 4: completion policies, practical exercises, evidence, sign-offs
// A policy lists what must be true before an assignment counts as complete;
// a practice is a practical exercise the learner submits evidence for. Both
// bind to a session or a track (track wins) and land on each participant's
// assignment. Evaluation is the server's — CompletionView.state is rendered as
// received, never re-derived here. Phase 6: a system_action requirement and a
// system-verified practice are satisfied by allow-listed platform events (see
// getSystemEvents); matching happens server-side when the event arrives.
export type RequirementKind = 'attendance' | 'score' | 'practice' | 'evidence' | 'manager_signoff' | 'trainer_signoff' | 'system_action';
export const REQUIREMENT_KINDS: RequirementKind[] = ['attendance', 'score', 'practice', 'evidence', 'manager_signoff', 'trainer_signoff', 'system_action'];
export type PolicyMode = 'all_required' | 'any_of';
export interface PolicyRequirement {
  kind: RequirementKind; threshold?: number | null;
  /** Phase 6, system_action only (400 otherwise): the platform event that satisfies it; absent = any verified platform activity. */
  event_type?: string;
}
export interface CompletionPolicy {
  id: string; name: string; mode: PolicyMode; requirements: PolicyRequirement[]; active: boolean; created_at: string;
}
export interface CompletionPolicyInput { name: string; mode: PolicyMode; requirements: PolicyRequirement[] }
export type VerificationKind = 'manual' | 'trainer' | 'manager' | 'system';
/** Phase 6: which platform event proves a system-verified practice. `match` constrains payload fields ("*" = present with any value); `actor_path` names the payload field that must equal the learner (default: the event's actor). */
export interface SystemRule { event_type: string; match?: Record<string, string> | null; actor_path?: string }
/** One allow-listed platform event (GET /system-events, manage|assign). */
export interface SystemEvent {
  event_type: string; source_service: string; label: string; description: string;
  /** Payload fields that may identify the learner (candidates for actor_path). */
  actor_fields: string[];
  /** Payload fields a rule may constrain. */
  match_hints: string[];
}
/** One recorded proof that the learner performed the action (Phase 6). */
export interface SystemVerification {
  id: string; practice_id?: string | null; event_type: string; event_id: string; source_service: string;
  aggregate_id?: string; occurred_at: string; snapshot: Record<string, unknown>;
}
export interface Practice {
  id: string; title: string; instructions?: string; evidence_required: boolean; score_required: boolean; min_score?: number | null;
  verification_kind: VerificationKind; system_rule?: SystemRule | null; active: boolean; created_at: string;
}
export interface PracticeInput {
  title: string; instructions?: string; evidence_required: boolean; score_required: boolean; min_score?: number | null;
  verification_kind: VerificationKind; system_rule?: SystemRule | null; active?: boolean;
}
export type SubmissionStatus = 'pending' | 'submitted' | 'validated' | 'rejected';
export interface Submission {
  id: string; practice_id: string; practice_title: string; assignment_id: string; session_id?: string | null; session_title: string;
  user_id: string; name: string; status: SubmissionStatus; evidence_url?: string; note?: string; score?: number | null;
  submitted_at?: string | null; validated_by?: string | null; validated_at?: string | null; validation_note?: string; verification_kind: VerificationKind;
}
export interface RequirementState { kind: RequirementKind; threshold?: number | null; met: boolean; detail?: string }
export interface CompletionState { mode: PolicyMode; satisfied: boolean; requirements: RequirementState[]; evaluated_at: string }
export interface Signoff { kind: 'manager' | 'trainer'; signed_by: string; signed_at: string; note?: string }
export interface CompletionView {
  assignment_id: string; status: Assignment['status'];
  policy?: CompletionPolicy | null; state?: CompletionState | null; practice?: Practice | null; submission?: Submission | null;
  signoffs: Signoff[]; evidence_url?: string; score?: number | null; unmet: string[]; overridden_by?: string | null;
  /** Phase 6: platform events that verified this assignment (practice and/or system_action requirement). */
  system_verifications?: SystemVerification[];
}
export interface EvidenceInput { evidence_url?: string; note?: string; score?: number }
export interface ValidateInput { decision: 'validated' | 'rejected'; score?: number; note?: string }
export interface SignoffInput { kind: 'manager' | 'trainer'; note?: string }

export const listCompletionPolicies = () => data<CompletionPolicy[]>(gatewayApi.get(`${BASE}/completion-policies`));
export const createCompletionPolicy = (b: CompletionPolicyInput) => data<CompletionPolicy>(gatewayApi.post(`${BASE}/completion-policies`, b));
export const updateCompletionPolicy = (id: string, b: Partial<CompletionPolicyInput> & { active?: boolean }) => data<CompletionPolicy>(gatewayApi.patch(`${BASE}/completion-policies/${id}`, b));
export const deleteCompletionPolicy = (id: string) => gatewayApi.delete(`${BASE}/completion-policies/${id}`).then((r) => r.data);
export const listPractices = () => data<Practice[]>(gatewayApi.get(`${BASE}/practices`));
/** Phase 6 (manage|assign): the allow-list of platform events that can verify practical work. */
export const getSystemEvents = () => data<SystemEvent[]>(gatewayApi.get(`${BASE}/system-events`));
export const createPractice = (b: PracticeInput) => data<Practice>(gatewayApi.post(`${BASE}/practices`, b));
export const updatePractice = (id: string, b: Partial<PracticeInput>) => data<Practice>(gatewayApi.patch(`${BASE}/practices/${id}`, b));
/** Owner | manage | assign-in-reach | session trainer; anyone else gets 404. */
export const getAssignmentCompletion = (assignmentId: string) => data<CompletionView>(gatewayApi.get(`${BASE}/assignments/${assignmentId}/completion`));
/** Record evidence/score on the assignment (owner or staff); the server re-evaluates and may auto-complete. */
export const submitEvidence = (assignmentId: string, b: EvidenceInput) => data<CompletionView>(gatewayApi.post(`${BASE}/assignments/${assignmentId}/evidence`, b));
/** Learner submits the practical exercise (owner only). 400 when required evidence/score is missing; manual practices validate immediately. */
export const submitPractice = (assignmentId: string, b: EvidenceInput) => data<CompletionView>(gatewayApi.post(`${BASE}/assignments/${assignmentId}/practice`, b));
export const validateSubmission = (submissionId: string, b: ValidateInput) => data<CompletionView>(gatewayApi.post(`${BASE}/practice-submissions/${submissionId}/validate`, b));
/** manager kind: assign-in-reach or manage; trainer kind: the session trainer or manage; never the learner. */
export const signoffAssignment = (assignmentId: string, b: SignoffInput) => data<CompletionView>(gatewayApi.post(`${BASE}/assignments/${assignmentId}/signoff`, b));
/** The validator queue (manage, or assign narrowed to reach). */
export const listSubmissions = (params: { session_id?: string; status?: SubmissionStatus | '' } = {}) =>
  data<Submission[]>(gatewayApi.get(`${BASE}/practice-submissions`, { params: { ...(params.session_id ? { session_id: params.session_id } : {}), ...(params.status ? { status: params.status } : {}) } }));

// --- L&D Phase 5: program templates, readiness, reporting -------------------
// A template is a reusable program definition (sessions at day offsets, tracks,
// practices, policies, readiness policy). Platform templates are read-only;
// tenant templates are versioned by key. Instantiation, cloning and readiness
// evaluation all happen server-side — nothing here derives readiness state.
export interface TemplatePractice { title: string; instructions?: string; verification_kind?: VerificationKind; evidence_required?: boolean; score_required?: boolean }
export interface TemplatePolicy { mode: PolicyMode; requirements: PolicyRequirement[] }
export interface TemplateTrack {
  name: string; objectives?: string; instructions?: string; assignment_type?: AssignmentType; rule?: RuleNode | null;
  practice?: TemplatePractice | null; policy?: TemplatePolicy | null;
}
export interface TemplateSession {
  title: string; description?: string; day_offset: number; start_time: string; duration_minutes: number;
  delivery_mode?: DeliveryMode; objectives?: string; critical?: boolean; audience_hint?: string; assignment_type?: AssignmentType;
  audience_rule?: RuleNode | null; tracks?: TemplateTrack[]; practice?: TemplatePractice | null; policy?: TemplatePolicy | null;
}
export interface ReadinessPolicy {
  enabled: boolean; attendance_pct: number; require_critical_sessions: boolean; require_practices: boolean; require_verification: boolean;
}
export interface TemplateDefinition {
  settings?: { audience_mode?: AudienceMode; default_assignment_type?: AssignmentType };
  readiness_policy?: ReadinessPolicy | null;
  sessions: TemplateSession[];
}
export interface ProgramTemplate {
  id: string; key: string; version: number; name: string; description?: string; program_type: string;
  /** true = shipped with the platform (read-only); false = this tenant's own. */
  platform: boolean; definition: TemplateDefinition; created_at: string;
}
export interface ProgramTemplateInput { key: string; name: string; description?: string; program_type?: string; definition: TemplateDefinition }
export interface FromTemplateInput { template_id: string; name?: string; starts_on: string; timezone: string }
export interface CloneProgramInput { name?: string; starts_on: string }
export interface SaveAsTemplateInput { key: string; name?: string }
export type ReadinessState = 'ready' | 'at_risk' | 'not_ready';
export interface ReadinessPerson {
  user_id: string; name: string; department: string; state: ReadinessState;
  required_sessions: number; attended_sessions: number; upcoming_sessions: number; missed_sessions: number;
  critical_total: number; critical_done: number; practices_total: number; practices_validated: number; verification_pending: number;
  blockers: string[];
}
export interface ReadinessDepartmentRow { department: string; ready: number; at_risk: number; not_ready: number; total: number }
export interface ProgramReadiness {
  program_id: string; policy?: ReadinessPolicy | null; state: ReadinessState;
  ready: number; at_risk: number; not_ready: number; total: number;
  by_department: ReadinessDepartmentRow[]; people: ReadinessPerson[]; computed_at: string;
}
export interface MyReadiness {
  program_id: string; program_name: string; state: ReadinessState; blockers: string[];
  required_sessions: number; attended_sessions: number; upcoming_sessions: number; practices_total: number; practices_validated: number;
}

export const listProgramTemplates = () => data<ProgramTemplate[]>(gatewayApi.get(`${BASE}/program-templates`));
export const getProgramTemplate = (id: string) => data<ProgramTemplate>(gatewayApi.get(`${BASE}/program-templates/${id}`));
/** New tenant key, or a new version of an existing tenant key (platform keys are read-only). */
export const createProgramTemplate = (b: ProgramTemplateInput) => data<ProgramTemplate>(gatewayApi.post(`${BASE}/program-templates`, b));
/** → DRAFT program with sessions placed at day_offset/start_time in `timezone`; the admin then sets the audience and publishes. */
export const createProgramFromTemplate = (b: FromTemplateInput) => data<Program>(gatewayApi.post(`${BASE}/programs/from-template`, b));
/** → DRAFT copy with dates shifted so the first session starts on `starts_on`; no participants. */
export const cloneProgram = (id: string, b: CloneProgramInput) => data<Program>(gatewayApi.post(`${BASE}/programs/${id}/clone`, b));
/** Tenant-owned template; re-saving the same key creates a new version. Tenant facets (departments…) become hints. */
export const saveProgramAsTemplate = (id: string, b: SaveAsTemplateInput) => data<ProgramTemplate>(gatewayApi.post(`${BASE}/programs/${id}/save-as-template`, b));
/** Body = the policy, or null to remove it. */
export const putReadinessPolicy = (programId: string, policy: ReadinessPolicy | null) => data<Program>(gatewayApi.put(`${BASE}/programs/${programId}/readiness-policy`, policy));
export const setSessionCritical = (sessionId: string, critical: boolean) => data<Session>(gatewayApi.put(`${BASE}/sessions/${sessionId}/critical`, { critical }));
/** manage | assign (people narrowed to reach). */
export const getProgramReadiness = (programId: string) => data<ProgramReadiness>(gatewayApi.get(`${BASE}/programs/${programId}/readiness`));
export const getMyReadiness = () => data<MyReadiness[]>(gatewayApi.get(`${BASE}/me/readiness`));
